import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { Button, Divider, Typography } from 'antd';
import { FormikProvider, useFormik } from 'formik';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { FieldInput } from '../login-formik-fields';
import { LOGIN_TYPE } from '~constants/auth';
import { loginContentTypes } from '../login-content';
import { useCheckLandlordExist } from '../useCheckLandlordExist';
import SvgFormat from '~helpers/svgFormat';
import event from '~helpers/mixpanel';

const useStyles = makeStyles({
  errText: {
    textAlign: 'left',
    color: '#F25202',
  },
  inputErr: {
    borderColor: '#F25202 !important',

    '&:hover': {
      borderColor: '#F25202 !important',
      boxShadow: 'none !important',
    },
  },
  divider: {
    fontSize: '14px',
    fontWeight: 'normal !important',
    marginTop: '40px !important',
    marginBottom: '20px !important',
  },
  btn: {},
  btnSvg: {
    height: 16,
    marginRight: 12,
    verticalAlign: 'text-bottom',
  },
});

const LoginForSetEmail = ({ handleNextContent, onChangeLoginType, clearLoginValue }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [email, setEmail] = useState();

  const onCompleted = e => {
    const showWarning = !e?.checkLandlordExist?.roles?.includes('LANDLORD');
    if (e?.checkLandlordExist?.exist) {
      handleNextContent(email, loginContentTypes.SET_EMAIL_PASSWORD, showWarning);
    } else {
      handleNextContent(email, loginContentTypes.EMAIL_FOR_SIGNUP, showWarning);
    }
  };

  const [queryCheckLandlordExist, loading] = useCheckLandlordExist({
    onCompleted,
  });

  const initialValues = useMemo(() => ({ email: '' }), []);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .label('Email')
          .email(t('landlord.auth.login.format_error.username'))
          .max(200)
          .required(t('this_part_cannot_be_empty')),
      }),
    [t],
  );

  const handleSubmit = async values => {
    event('Clicked Email Page', {
      'Clicked Position': 'Continue Btn',
    });

    await queryCheckLandlordExist({
      variables: {
        type: LOGIN_TYPE.EMAIL,
        value: values?.email,
      },
    });
    setEmail(values?.email);
  };

  const handleTogglePhoneSignup = () => {
    event('Clicked Email Page', {
      'Clicked Position': 'Phone Method',
    });

    clearLoginValue();
    onChangeLoginType(loginContentTypes.SET_PHONE_NUMBER);
  };

  const handleEmailFocus = () => {
    event('Clicked Email Page', {
      'Clicked Position': 'Email Box',
    });
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    validateOnBlur: false,
  });

  useEffect(() => {
    event('Viewed Email Page');
  }, []);

  return (
    <FormikProvider value={formik}>
      <FieldInput
        fieldConfig="email"
        placeholder={t('email_address')}
        style={{ marginTop: 40 }}
        onFocus={handleEmailFocus}
      />
      <Button type="primary" size="large" loading={loading} onClick={formik.submitForm}>
        {t('continue')}
      </Button>
      <Divider className={classes.divider}>{t('or')}</Divider>
      <Button
        size="large"
        style={{ border: '1px solid ', color: 'black', opacity: 0.85 }}
        onClick={handleTogglePhoneSignup}
      >
        <SvgFormat type="sign-up-phone" className={classes.btnSvg} />
        {t('continue_with_phone_number')}
      </Button>
      <Typography>
        {t('you_will_be_contacted_by_our_staff_to_agree_to_our_terms_and_conditions')}
      </Typography>
    </FormikProvider>
  );
};

LoginForSetEmail.propTypes = {
  onChangeLoginType: PropTypes.func.isRequired,
  handleNextContent: PropTypes.func.isRequired,
  clearLoginValue: PropTypes.func.isRequired,
};

export default LoginForSetEmail;
